import React, { useState, useMemo } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { FiChevronDown, FiChevronUp, FiSearch, FiX } from 'react-icons/fi';

const INITIAL_ITEMS_COUNT = 5; // Show first 5 items initially

/**
 * Collapsible filter panel for screener categories with load more functionality
 */
const FilterPanel = ({ screeners, selectedFilter, onFilterSelect, onReset }) => {
  const { theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    candlesticks: true,
    scanners: false,
    technicals: false,
  });
  const [loadedItemsCount, setLoadedItemsCount] = useState({
    candlesticks: INITIAL_ITEMS_COUNT,
    scanners: INITIAL_ITEMS_COUNT,
    technicals: INITIAL_ITEMS_COUNT,
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      // If clicking on already expanded category, collapse it
      if (prev[category]) {
        return {
          ...prev,
          [category]: false,
        };
      }
      // Otherwise, collapse all and expand only the clicked one
      return {
        candlesticks: false,
        scanners: false,
        technicals: false,
        [category]: true,
      };
    });

    // Reset the loaded items count to initial value when expanding a category
    setLoadedItemsCount(prev => ({
      ...prev,
      [category]: INITIAL_ITEMS_COUNT,
    }));
  };

  const loadMoreItems = (category) => {
    setLoadedItemsCount(prev => ({
      ...prev,
      [category]: prev[category] + INITIAL_ITEMS_COUNT,
    }));
  };

  // Filter screeners based on search term - only searches in pattern names
  const filteredScreeners = useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return screeners;

    const lowerSearch = trimmedSearch.toLowerCase();

    return Object.keys(screeners).reduce((acc, category) => {
      const filtered = screeners[category]?.data?.filter(item => {
        const heading = (item.heading || item.name || '').toLowerCase();

        // Check if the heading contains the search term
        return heading.includes(lowerSearch);
      }) || [];

      if (filtered.length > 0) {
        acc[category] = {
          ...screeners[category],
          data: filtered,
        };
      }
      return acc;
    }, {});
  }, [screeners, searchTerm]);

  const categoryLabels = {
    candlesticks: 'Candlesticks',
    scanners: 'Scanners',
    technicals: 'Technicals',
  };

  return (
    <div className={`w-[305px] mt-3 rounded-[6px] overflow-hidden ${theme === 'dark'
      ? 'bg-[#0F0F0F] shadow-[0_2px_5px_1px_rgba(175,175,175,0.1)]'
      : 'bg-white shadow-[0_2px_5px_1px_rgba(87,87,87,0.1)]'
      }`}
      style={{ maxHeight: 'calc(100vh - 5rem)' }}
    >
      {/* Header */}
      <div className="px-3 pt-3">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xs font-bold ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
            FILTERS
          </h2>
          <button
            onClick={onReset}
            className="text-xs font-bold text-[#E94C69]"
          >
            Reset
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <FiSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-[#979797]' : 'text-[#6F6F6F]'
            }`} />
          <input
            type="text"
            placeholder="Search candlestick scanner.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full h-[50px] pl-12 pr-10 rounded-[10px] border-2 text-sm font-medium ${theme === 'dark'
              ? 'bg-[#1F1F1F] border-[#1F1F1F] text-white placeholder-[#676767] focus:border-[#1F1F1F]'
              : 'bg-[#EBEBEB] border-[#EBEBEB] text-black placeholder-[#9F9F9F] focus:border-[#EBEBEB]'
              } focus:outline-none`}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-[#979797]' : 'text-[#6F6F6F]'
                }`}
            >
              <FiX />
            </button>
          )}
        </div>
      </div>

      {/* Filter Categories */}
      <div className="px-3 pb-3 overflow-y-auto no-scrollbar " style={{ maxHeight: 'calc(100vh - 12rem)' }}>
        {Object.keys(filteredScreeners).map((category) => {
          const categoryData = filteredScreeners[category];
          const isExpanded = expandedCategories[category];
          const allItems = categoryData?.data || [];
          const visibleItemsCount = loadedItemsCount[category];
          const visibleItems = allItems.slice(0, visibleItemsCount);
          const hasMoreItems = allItems.length > visibleItemsCount;

          return (
            <div key={category} className="mb-4">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-[6px] text-sm font-medium ${theme === 'dark'
                  ? 'bg-[#1F1F1F] text-white'
                  : 'bg-[#EBEBEB] text-black'
                  }`}
              >
                <span className="capitalize">
                  {categoryLabels[category]}
                </span>
                {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>

              {/* Category Items */}
              {isExpanded && (
                <div className="mt-1 space-y-0.5">
                  {visibleItems.map((item, index) => {
                    // Support multiple property names for the heading
                    const itemName = item.heading || item.name || item.title || item.label || 'Unknown';
                    const isSelected = selectedFilter?.path === category && selectedFilter?.name === itemName;

                    return (
                      <button
                        key={`${category}-${itemName}-${index}`}
                        onClick={() => onFilterSelect({ path: category, name: itemName, data: item })}
                        className={`w-full text-left px-3 py-2 rounded-[6px] text-[13px] font-medium text-n500`}
                      >
                        <div className="flex items-center">
                          {/* Radio Button */}
                          <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${isSelected
                            ? 'border-[#0075FF]'
                            : 'border-n500'
                            }`}>
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-[#0075FF]" />
                            )}
                          </div>
                          <span>{itemName}</span>
                        </div>
                      </button>
                    );
                  })}

                  {/* Load More Button */}
                  {hasMoreItems && (
                    <button
                      onClick={() => loadMoreItems(category)}
                      className={`w-full text-left px-3 py-2 text-sm font-bold ${theme === 'dark'
                        ? 'text-[#0075FF] hover:text-[#3399FF]'
                        : 'text-[#0075FF] hover:text-[#0055DD]'
                        }`}
                    >
                      + Load More
                    </button>
                  )}

                  {allItems.length === 0 && (
                    <p className={`px-3 py-2 text-sm ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
                      No filters found
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FilterPanel;